import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { colors } from '../../../theme';

// ─── Social brand icons (SVG inline) ────────────────────────────────────────

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#1877F2"
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#000000"
      d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
    />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────

const AuthChoicePage: React.FC = () => {
  const navigate = useNavigate();

  const SOCIAL_BUTTONS = [
    {
      label: 'Continue with Google',
      icon: <GoogleIcon />,
      bgcolor: 'white',
      color: '#3c4043',
      border: `1px solid #dadce0`,
    },
    {
      label: 'Continue with Facebook',
      icon: <FacebookIcon />,
      bgcolor: '#1877F2',
      color: 'white',
      border: 'none',
    },
    {
      label: 'Continue with Apple',
      icon: <AppleIcon />,
      bgcolor: '#000000',
      color: 'white',
      border: 'none',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 4,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: colors.primary.main,
                letterSpacing: '-0.5px',
              }}
            >
              IQMaxx
            </Typography>
          </Box>

          {/* Heading */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              color: colors.text.primary,
              mb: 0.5,
            }}
          >
            Join IQMaxx
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 3 }}
          >
            Create an account or sign in to continue
          </Typography>

          {/* Social buttons — UI only, no functionality */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
            {SOCIAL_BUTTONS.map((btn) => (
              <Button
                key={btn.label}
                fullWidth
                variant="outlined"
                disabled
                startIcon={btn.icon}
                sx={{
                  bgcolor: btn.bgcolor,
                  color: `${btn.color} !important`,
                  border: btn.border,
                  borderRadius: 2,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  justifyContent: 'flex-start',
                  pl: 3,
                  opacity: 0.65,
                  cursor: 'not-allowed',
                  '&.Mui-disabled': {
                    bgcolor: btn.bgcolor,
                    color: `${btn.color} !important`,
                    border: btn.border,
                  },
                  '& .MuiButton-startIcon': { mr: 1.5 },
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 2.5 }}>
            <Typography variant="caption" sx={{ color: colors.text.secondary, px: 1 }}>
              or continue with email
            </Typography>
          </Divider>

          {/* Email action buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 16px rgba(74,144,226,0.35)',
                '&:hover': {
                  bgcolor: colors.primary.dark,
                  boxShadow: '0 6px 20px rgba(74,144,226,0.45)',
                },
              }}
            >
              Sign Up with Email
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: colors.primary.main,
                color: colors.primary.main,
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                '&:hover': {
                  bgcolor: colors.primary.lightest,
                  borderColor: colors.primary.dark,
                },
              }}
            >
              Sign In with Email
            </Button>
          </Box>

          {/* Footer note */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: colors.text.secondary,
              mt: 3,
              lineHeight: 1.6,
            }}
          >
            By continuing you agree to our{' '}
            <Box
              component="span"
              sx={{ color: colors.primary.main, cursor: 'pointer', fontWeight: 600 }}
            >
              Terms of Service
            </Box>{' '}
            and{' '}
            <Box
              component="span"
              sx={{ color: colors.primary.main, cursor: 'pointer', fontWeight: 600 }}
            >
              Privacy Policy
            </Box>
            .
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthChoicePage;
